import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  InputAdornment,
  TextField,
  Slider,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DateRangeFilter from './DateRangeFilter';
import { FILTER_HEIGHT } from './ui/filterStyles';
import { useSearchParams, useParams } from 'react-router-dom';
import { parseISO, sub, isBefore, isValid } from 'date-fns';
import { useOrganizationMetadata } from '../hooks/useBrands';

// Org start/end dates are free text in the admin form, and URL params can be edited by hand.
// Anything that doesn't parse falls back instead of becoming an Invalid Date (which crashed the page).
const parseDateOr = (value: string | null | undefined, fallback?: Date): Date | undefined => {
  if (!value) return fallback;
  const parsed = parseISO(value);
  if (isValid(parsed)) return parsed;
  const loose = new Date(value);
  return isValid(loose) ? loose : fallback;
};

interface ImageFilterSetProps {
  onDatesChange?: (startDate: Date | null, endDate: Date | null) => void;
}

const ImageFilterSet: React.FC<ImageFilterSetProps> = ({ onDatesChange }) => {
  const { orgId: orgIdStr } = useParams<{ orgId: string }>();
  const orgId = parseInt(orgIdStr || '0', 10);
  const { metadata: orgMetadata } = useOrganizationMetadata(orgId);
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get initial values from URL params
  const getInitialParam = (key: string, defaultValue: string = ''): string => {
    const param = searchParams.get(key);
    return param || defaultValue;
  };

  const getInitialNumberParam = (key: string, defaultValue: number): number => {
    const param = searchParams.get(key);
    return param ? parseInt(param, 10) : defaultValue;
  };

  // Initialize dates from URL or defaults, similar to FilterContext
  const initialDates = useMemo(() => {
    const endParam = searchParams.get('endDate');
    
    // Define a fallback start date if metadata is not available or lacks data_start_date
    const fallbackStartDate = sub(new Date(), { months: 6 });
    const defaultStartDate = parseDateOr(orgMetadata?.data_start_date, fallbackStartDate) as Date;
    const endDate = (parseDateOr(endParam) ?? parseDateOr(orgMetadata?.data_end_date, new Date())) as Date;
    const sixtyDaysAgo = sub(endDate, { days: 60 });

    // If sixtyDaysAgo is before data_start_date, use data_start_date
    const effectiveDefaultStart = isBefore(sixtyDaysAgo, defaultStartDate) ? defaultStartDate : sixtyDaysAgo;

    return { start: effectiveDefaultStart, end: endDate };
  }, [searchParams, orgMetadata]);

  // State for the filters
  const [startDate, setStartDate] = useState<Date | null>(initialDates.start);
  const [endDate, setEndDate] = useState<Date | null>(initialDates.end);
  const [keywordSearch, setKeywordSearch] = useState<string>(getInitialParam('keyword'));
  const [minFeatureCount, setMinFeatureCount] = useState<number>(getInitialNumberParam('minFeatureCount', 1));
  const [minEngagementScore, setMinEngagementScore] = useState<number>(getInitialNumberParam('minEngagementScore', 0));

  // Update URL params when filters change, with debounce
  const updateUrlParams = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    
    // Update dates
    if (startDate && isValid(startDate)) {
      newParams.set('startDate', startDate.toISOString());
    } else {
      newParams.delete('startDate');
    }
    
    if (endDate && isValid(endDate)) {
      newParams.set('endDate', endDate.toISOString());
    } else {
      newParams.delete('endDate');
    }

    // Update keyword search
    if (keywordSearch) {
      newParams.set('keyword', keywordSearch);
    } else {
      newParams.delete('keyword');
    }

    // Update minimum feature count
    if (minFeatureCount > 1) {
      newParams.set('minFeatureCount', minFeatureCount.toString());
    } else {
      newParams.delete('minFeatureCount');
    }
    
    // Update minimum engagement score
    if (minEngagementScore > 0) {
      newParams.set('minEngagementScore', minEngagementScore.toString());
    } else {
      newParams.delete('minEngagementScore');
    }
    
    // Replace rather than push, and only when something changed: each push left another
    // Image Comparison entry in the browser history, so Back appeared to do nothing.
    if (newParams.toString() !== searchParams.toString()) {
      setSearchParams(newParams, { replace: true });
    }
    
    console.log('ImageFiltersChanged', {
      startDate,
      endDate,
      keywordSearch,
      minFeatureCount,
      minEngagementScore
    });
  }, [searchParams, startDate, endDate, keywordSearch, minFeatureCount, minEngagementScore, setSearchParams]);

  // Update URL when filters change
  useEffect(() => {
    updateUrlParams();
  }, [updateUrlParams]);

  // Parse URL params when they change, without depending on current state
  useEffect(() => {
    const startDateParam = searchParams.get('startDate');
    const endDateParam = searchParams.get('endDate');
    const keywordParam = searchParams.get('keyword');
    const minFeatureCountParam = searchParams.get('minFeatureCount');
    const minEngagementScoreParam = searchParams.get('minEngagementScore');

    // Parse dates as Date objects when present
    if (startDateParam !== null) {
      const newStartDate = new Date(startDateParam);
      if (!isNaN(newStartDate.getTime())) {
        setStartDate(newStartDate);
      }
    }
    
    if (endDateParam !== null) {
      const newEndDate = new Date(endDateParam);
      if (!isNaN(newEndDate.getTime())) {
        setEndDate(newEndDate);
      }
    }

    if (keywordParam !== null) {
      setKeywordSearch(keywordParam);
    }

    if (minFeatureCountParam !== null) {
      const value = parseInt(minFeatureCountParam, 10);
      if (!isNaN(value)) {
        setMinFeatureCount(value);
      }
    }
    
    if (minEngagementScoreParam !== null) {
      const value = parseInt(minEngagementScoreParam, 10);
      if (!isNaN(value)) {
        setMinEngagementScore(value);
      }
    }
  }, [searchParams]); // Only depend on searchParams, not on the state variables

  // Notify parent component when dates change
  useEffect(() => {
    if (onDatesChange) {
      onDatesChange(startDate, endDate);
    }
  }, [startDate, endDate, onDatesChange]);

  const handleDateChange = (newStartDate: Date | null, newEndDate: Date | null) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const handleKeywordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setKeywordSearch(event.target.value);
  };

  const handleMinFeatureCountChange = (_event: Event, newValue: number | number[]) => {
    setMinFeatureCount(newValue as number);
  };
  
  const handleMinEngagementScoreChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    setMinEngagementScore(isNaN(value) ? 0 : value);
  };

  // Define minDate safely
  const safeMinDate = parseDateOr(orgMetadata?.data_start_date);
  const safeMaxDate = parseDateOr(orgMetadata?.data_end_date, new Date());

  const fieldLabel = (text: string) => (
    <InputAdornment position="start" sx={{ color: 'text.secondary', '& p': { fontSize: '0.8125rem', fontWeight: 500 } }}>
      <Typography component="span" sx={{ fontSize: '0.8125rem', fontWeight: 500, color: 'text.secondary' }}>
        {text}
      </Typography>
    </InputAdornment>
  );

  // Same one-row control bar as the main FilterSet, with the image-specific
  // filters in place of brands/platforms/pillars.
  return (
    <Box
      role="group"
      aria-label="Image filters"
      sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, justifyContent: { lg: 'flex-end' } }}
    >
      <Box sx={{ flex: '1 1 270px', maxWidth: { sm: 340 } }}>
        <DateRangeFilter
          startDate={startDate}
          endDate={endDate}
          onChange={handleDateChange}
          minDate={safeMinDate}
          maxDate={safeMaxDate}
        />
      </Box>

      <TextField
        size="small"
        placeholder="Search keywords"
        value={keywordSearch}
        onChange={handleKeywordChange}
        inputProps={{ 'aria-label': 'Keyword search' }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            </InputAdornment>
          ),
          sx: { height: FILTER_HEIGHT, fontSize: '0.875rem' },
        }}
        sx={{ flex: '1 1 190px' }}
      />

      <TextField
        size="small"
        type="number"
        value={minEngagementScore}
        onChange={handleMinEngagementScoreChange}
        inputProps={{ min: 0, 'aria-label': 'Minimum engagement score' }}
        InputProps={{
          startAdornment: fieldLabel('Min engagement'),
          sx: { height: FILTER_HEIGHT, fontSize: '0.875rem', fontWeight: 600 },
        }}
        sx={{ flex: '1 1 170px' }}
      />

      <Box
        sx={(theme) => ({
          flex: '1 1 210px',
          height: FILTER_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 1.5,
          bgcolor: 'background.paper',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
        })}
      >
        <Typography
          id="min-feature-count-slider"
          component="span"
          sx={{ fontSize: '0.8125rem', fontWeight: 500, color: 'text.secondary', whiteSpace: 'nowrap' }}
        >
          Min features
        </Typography>
        <Slider
          size="small"
          min={0}
          max={20}
          step={1}
          value={minFeatureCount}
          onChange={handleMinFeatureCountChange}
          valueLabelDisplay="auto"
          aria-labelledby="min-feature-count-slider"
          sx={{ flex: 1 }}
        />
        <Typography component="span" sx={{ fontSize: '0.875rem', fontWeight: 600, minWidth: 18, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
          {minFeatureCount}
        </Typography>
      </Box>
    </Box>
  );
};

export default ImageFilterSet; 